import tagColors from '../lib/tagColors';

export type RGB = {
  r: number;
  g: number;
  b: number;
};

/**
 * Calculates the relative luminance of a given hex color string based on the
 * sRGB color space.
 *
 * @param {string} hex - A hexadecimal color string (e.g., "#RRGGBB"*/
export function getLuminance(hex: string): number {
  // Remove the hash and normalize the string
  let cleanHex = hex.replace(/^#/, '');

  // Support short notation (#fff)
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // Must be 6 characters long
  if (cleanHex.length !== 6) {
    return 0;
  }

  const num = parseInt(cleanHex, 16);
  if (isNaN(num)) return 0;

  // Extract r, g, b explicitly and safely
  const r: number = (num >> 16) & 0xff;
  const g: number = (num >> 8) & 0xff;
  const b: number = num & 0xff;

  // Force type as number
  const [lr, lg, lb]: [number, number, number] = [r, g, b].map((value) => {
    const scaled = value / 255;
    return scaled <= 0.03928 ? scaled / 12.92 : Math.pow((scaled + 0.055) / 1.055, 2.4);
  }) as [number, number, number];

  return 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;
}

/**
 * Converts a hexadecimal color code to an RGB object.
 *
 * @param {string} hex - The hexadecimal color code, which can be in a 3- or 6-character format.
 * @return {RGB | null} An object containing the red (r), green (g), and blue (b) values,
 * or null if the input is not a valid hexadecimal color code.
 */
export function hexToRgb(hex: string): RGB | null {
  const clean = (hex || '').replace(/^#/, '').trim().toLowerCase();

  if (clean.length === 6) {
    const bigint = parseInt(clean, 16);
    if (isNaN(bigint)) return null;
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  }

  if (clean.length === 3) {
    // TypeScript qui vuole garanzia: forziamo il tipo dell’array
    const chars = clean.split('') as [string, string, string];
    return {
      r: parseInt(chars[0] + chars[0], 16),
      g: parseInt(chars[1] + chars[1], 16),
      b: parseInt(chars[2] + chars[2], 16),
    };
  }

  return null;
}

/**
 * Calculates the contrast ratio between two colors based on their luminance values.
 *
 * @param color1 The first color in a string format (e.g., hexadecimal, RGB, or any format supported by getLuminance).
 * @param color2 The second color in a string format (e.g., hexadecimal, RGB, or any format supported by getLuminance).
 * @return A number representing the contrast ratio between the two colors. Higher values indicate greater contrast.
 */
export function getContrastRatio(color1: string, color2: string): number {
  const luminance1 = getLuminance(color1) + 0.05;
  const luminance2 = getLuminance(color2) + 0.05;
  return luminance1 > luminance2 ? luminance1 / luminance2 : luminance2 / luminance1;
}

function lightenColor({ r, g, b }: { r: number; g: number; b: number }, amount: number) {
  return {
    r: r + ((255 - r) * amount) / 100,
    g: g + ((255 - g) * amount) / 100,
    b: b + ((255 - b) * amount) / 100,
  };
}

function darkenColor({ r, g, b }: { r: number; g: number; b: number }, amount: number) {
  return {
    r: r * (1 - amount / 100),
    g: g * (1 - amount / 100),
    b: b * (1 - amount / 100),
  };
}

function desaturateColor({ r, g, b }: { r: number; g: number; b: number }, factor: number) {
  const gray = r * 0.3 + g * 0.59 + b * 0.11;
  return {
    r: r + factor * (gray - r),
    g: g + factor * (gray - g),
    b: b + factor * (gray - b),
  };
}

function rgbToHex({ r, g, b }: { r: number; g: number; b: number }): string {
  return (
    '#' +
    [r, g, b]
      .map((x) =>
        Math.max(0, Math.min(255, Math.round(x)))
          .toString(16)
          .padStart(2, '0'),
      )
      .join('')
  );
}

/**
 * Determines the background color (either light or dark) that provides the best contrast for the given text color.
 *
 * @param {string} textColor - The text color in a valid color format (e.g., HEX, RGB).
 * @return {string} The background color (either light or dark) that maximizes contrast with the provided text color.
 */
export function getContrastBackgroundColorForText(
  textColor: string,
  options?: {
    pastel?: boolean;
    minContrast?: number;
  },
): string {
  // if the background has already been calculated
  const defaultValues = tagColors.find((c) => c.text === textColor);
  if (defaultValues) {
    return defaultValues.bg;
  }

  const pastel = options?.pastel ?? true;
  const minContrast = options?.minContrast ?? 7;

  const rgb = hexToRgb(textColor);
  if (!rgb) {
    return '#000000';
  }

  const luminance = getLuminance(textColor);
  const isDark = luminance < 0.5;

  let bgRgb = { ...rgb };

  // Pastel effect
  if (pastel) {
    bgRgb = isDark ? lightenColor(bgRgb, 85) : darkenColor(desaturateColor(bgRgb, 0.3), 10);
  } else {
    bgRgb = isDark ? lightenColor(rgb, 40) : darkenColor(rgb, 20);
  }

  // Ensure contrast
  let iterations = 0;
  while (getContrastRatio(rgbToHex(rgb), rgbToHex(bgRgb)) < minContrast && iterations < 10) {
    bgRgb = isDark ? lightenColor(bgRgb, 8) : darkenColor(bgRgb, 5);
    iterations++;
  }

  return rgbToHex(bgRgb);
}
